import { ApolloClient } from '@apollo/client'
import { slugs } from '@/constants/slugs'
import { GET_ALL_MOVIES, GET_ALL_SHOWS, GET_OUR_SHOWS } from '@/services/ProjectsService/queries'
import { ContentMovie, ContentSeries, GetOurShowsQuery } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'

export type NowStreamingContent = ContentMovie | ContentSeries

export async function getNowStreamingProjects(client: ApolloClient<object>): Promise<NowStreamingContent[] | null> {
  const { data } = await client.query<GetOurShowsQuery>({
    query: GET_OUR_SHOWS,
    variables: {
      exclude: [
        slugs.insideAngelStudios,
        slugs.angelStudios,
        slugs.angelOldChosen,
        slugs.forYourConsideration,
        slugs.theChosen,
        slugs.stagingForContent,
        'jungle-beat-staging',
      ],
    },
    errorPolicy: 'all',
  })
  if (!data?.listWatchables?.edges) return []

  return data?.listWatchables?.edges
    .filter((edge) => {
      const watchableNode = edge?.node as ContentSeries | ContentMovie
      const project = watchableNode?.project
      const image = watchableNode.image

      return (
        Boolean(project?.slug) &&
        Boolean(image?.cloudinaryPath) &&
        Boolean(project.releaseDate) &&
        new Date(project.releaseDate).getTime() < new Date().getTime()
      )
    })
    .map((edge) => edge?.node as NowStreamingContent)
}

export async function getAllMovies(client: ApolloClient<object>): Promise<ContentMovie[]> {
  try {
    const { data } = await client.query<GetOurShowsQuery>({
      query: GET_ALL_MOVIES,
      variables: {
        exclude: [
          slugs.insideAngelStudios,
          slugs.angelStudios,
          slugs.angelOldChosen,
          slugs.forYourConsideration,
          slugs.theChosen,
          slugs.stagingForContent,
          'jungle-beat-staging',
        ],
      },
      errorPolicy: 'all',
    })

    if (!data?.listWatchables?.edges) {
      logger().warn('getAllMovies: No watchable edges found in response')
      return []
    }

    return data.listWatchables.edges
      .filter((edge) => {
        const watchableNode = edge?.node as ContentMovie
        const project = watchableNode?.project
        const image = watchableNode.image

        return (
          Boolean(project?.slug) &&
          Boolean(image?.cloudinaryPath) &&
          Boolean(project.releaseDate) &&
          new Date(project.releaseDate).getTime() < new Date().getTime()
        )
      })
      .map((edge) => edge?.node as ContentMovie)
      .sort((a, b) => (a?.project?.sortOrder ?? 1000) - (b?.project?.sortOrder ?? 1000))
  } catch (error) {
    logger().error('getAllMovies: Failed to fetch all movies', { error })
    return []
  }
}

export async function getAllShows(client: ApolloClient<object>): Promise<ContentSeries[]> {
  try {
    const { data } = await client.query<GetOurShowsQuery>({
      query: GET_ALL_SHOWS,
      variables: {
        exclude: [
          slugs.insideAngelStudios,
          slugs.angelStudios,
          slugs.angelOldChosen,
          slugs.forYourConsideration,
          slugs.theChosen,
          slugs.stagingForContent,
          'jungle-beat-staging',
        ],
      },
      errorPolicy: 'all',
    })

    if (!data?.listWatchables?.edges) {
      logger().warn('getAllShows: No watchable edges found in response')
      return []
    }

    return data.listWatchables.edges
      .filter((edge) => {
        const watchableNode = edge?.node as ContentSeries
        const project = watchableNode?.project
        const image = watchableNode.image

        return (
          Boolean(project?.slug) &&
          Boolean(image?.cloudinaryPath) &&
          Boolean(project.releaseDate) &&
          new Date(project.releaseDate).getTime() < new Date().getTime()
        )
      })
      .map((edge) => edge?.node as ContentSeries)
      .sort((a, b) => (a?.project?.sortOrder ?? 1000) - (b?.project?.sortOrder ?? 1000))
      .filter(isDefined)
  } catch (error) {
    logger().error('getAllShows: Failed to fetch all shows', { error })
    return []
  }
}
